import React, {
  ReactElement,
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Flex } from 'rebass/styled-components';
import Icon from 'assets/icons';
import { useHistory, useParams } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from 'react-hook-form';

import FileImage from 'containers/ProcedureBuilder/FileImage';

import { Alert } from 'ui/patterns';

import { FormInput, FormSelect, FormGroup, Card, Loader } from 'ui/patterns';
import {
  Grid,
  Stack,
  Checkbox,
  RadioButton,
  Button,
  FormLabel,
  TinyMCE,
  Dropzone,
  StyledSelect,
} from 'ui/components';
import { CheckboxGrid } from './styles';

import {
  useGetAccountServices,
  useGetAccountStatus,
  useCreateBlobDirectory,
  useDeleteBlobFile,
  useGetAccountInfo,
  useGetProcedureUrlList,
  useGetRelationshipManagers,
  useSaveAccountInfo,
  useGetStateProvince,
  useGetTimezones,
  useUploadExternalDocument,
  useUploadFile,
} from 'hooks';
import usePrompt from 'hooks/usePrompt';

import { confirmAlert } from 'utils/confirm-alert';
import { showErrorToast, showSuccessToast } from 'utils/showToast';
import { apiBaseUrl } from 'utils/urls';
import { useApplicationThemeStore } from '../../../store/useApplicationThemeStore';

import { BlobType } from 'types/procedureBuilder';
import { AccountFormInputs } from 'types/accounts';
import { TResult } from 'hooks/useSaveAccountInfo';
import { ErrorModal } from 'types/modalTypes';

const empty = {
  accountInfoReturn: undefined,
  accountFormInputs: undefined,
};

const formDefaults = {
  AnswerScript: '',
  AnswerScriptDetailType: 1,
  AnswerScriptDetails: '',
  AutoPurgeDeadlineInHours: 0,
  CID: '',
  City: '',
  Id: null,
  IsAutoPurgeEnabled: false,
  IsLive: false,
  IsPriorityFinalization: false,
  Name: '',
  PID: '',
  SID: '',
  ReportingDisplayName: '',
  VDN: '',
  ExternalFile: '',
  PersonAlertLimiter: false,
  LimitCategory: '',
  AccountCategoryLimitingValues: [
    {
      Id: 0,
      Name: '',
      AccountId: null,
    },
  ],
};

function useHelperHook(id: number) {
  const { data: stateProvinces, isLoading: l1 } = useGetStateProvince();
  const { data: relationshipManagers, isLoading: l2 } =
    useGetRelationshipManagers();
  const { data: timezones, isLoading: l3 } = useGetTimezones();
  const { data: accountStatuses, isLoading: l4 } = useGetAccountStatus();
  const { data: accountServices, isLoading: l5 } = useGetAccountServices();
  const { data: accountInfo, isLoading: l6 } = useGetAccountInfo(id);
  const { data: procedures, isLoading: l7 } = useGetProcedureUrlList(id);
  return {
    accountInfo,
    procedures,
    stateProvinces,
    relationshipManagers,
    timezones,
    accountStatuses,
    accountServices,
    isLoading: l1 || l2 || l3 || l4 || l5 || l6 || l7,
  };
}

export default function AccountInformation(): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);

  const [isPageDirty, setDirty] = useState<boolean>();
  const [isTinyMCELoading, setTinyMCELoading] = useState(false);
  const [externalFile, setExternalFile] = useState<string | null>();
  const [purgePermission, setPurgePermission] = useState(false);
  const [showFileImage, setShowFileImage] = useState(false);

  const { accountId } = useParams<{
    accountId: string;
    tabId?: string;
  }>();

  const history = useHistory();
  const permission = JSON.parse(localStorage.getItem('userPermissions') ?? '');

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    errors,
    setValue,
    trigger,
    getValues,
    formState: { isDirty },
  } = useForm<AccountFormInputs>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      ...formDefaults,
    },
  });

  const limiterOptions = watch('AccountCategoryLimitingValues');
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'AccountCategoryLimitingValues',
  });

  const answerScriptDetailType = watch('AnswerScriptDetailType');
  const isAutoPurgeEnabled = watch('IsAutoPurgeEnabled');
  const AutoPurgeDeadlineInHours = watch('AutoPurgeDeadlineInHours');
  const isLiveEnabled = watch('IsLive');
  const isAlertLimitEnabled = watch('PersonAlertLimiter');


  const { mutate: saveAccountInfo, isLoading: isSaveAccountInfoLoading } =
    useSaveAccountInfo();

  const {
    accountInfo,
    stateProvinces,
    relationshipManagers,
    timezones,
    accountStatuses,
    accountServices,
    procedures,
    isLoading,
  } = useHelperHook(parseInt(accountId));

  const {
    mutate: createBlobDirectory,
    isLoading: isCreateBlobDirectoryLoading,
  } = useCreateBlobDirectory();
  const { mutate: uploadFile, isLoading: isuploadFileLoading } =
    useUploadFile();
  const { mutate: deleteFile, isLoading: isDeleteFileLoading } =
    useDeleteBlobFile();

  const isLoadingFile =
    isCreateBlobDirectoryLoading || isuploadFileLoading || isDeleteFileLoading;
  const isLoadingInfo = isSaveAccountInfoLoading || isLoading;
  const { accountInfoReturn, accountFormInputs } = accountInfo || empty;

  useEffect(() => {
    const permissions = localStorage.getItem('userPermissions');
    if (permissions) {
      const temp = JSON.parse(permissions);
      setPurgePermission(temp.canPurgeAccount);
    }
  }, []);

  useEffect(() => {
    if (accountFormInputs && !isLoadingInfo) {
      reset({ ...accountFormInputs });
      setTimeout(() => {
        setDirty(false);
      }, 500);
    }
  }, [accountFormInputs, reset, isLoadingInfo, setDirty]);


  useEffect(() => {
    if (!isTinyMCELoading) setDirty(isDirty);
  }, [setDirty, isDirty, isTinyMCELoading]);

  useEffect(() => {
    if (
      accountInfoReturn &&
      accountInfoReturn.Data.AnswerScriptDetailType === answerScriptDetailType
    )
      setValue(
        'AnswerScriptDetails',
        accountInfoReturn.Data.AnswerScriptDetails,
        {
          shouldDirty: false,
        },
      );
    else
      setValue('AnswerScriptDetails', '', {
        shouldDirty: false,
      });
  }, [answerScriptDetailType, accountInfoReturn, setValue]);

  useEffect(() => {
    if (accountInfoReturn?.Data.IsAutoPurgeEnabled && isAutoPurgeEnabled)
      setValue(
        'AutoPurgeDeadlineInHours',
        accountInfoReturn?.Data.AutoPurgeDeadlineInHours,
        {
          shouldDirty: false,
        },
      );
    else
      setValue('AutoPurgeDeadlineInHours', 72, {
        shouldDirty: false,
      });
  }, [accountInfoReturn, isAutoPurgeEnabled, setValue]);

  const saveFormApi = (data: AccountFormInputs, infoMessage: boolean) => {
    if (accountInfoReturn !== undefined) {
      data.RelationshipManagerDto = {
        Id: accountInfoReturn.Data.RelationshipManager?.Id,
        FirstName: accountInfoReturn.Data.RelationshipManager?.FirstName,
        LastName: accountInfoReturn.Data.RelationshipManager?.LastName,
      };
    }
    if (externalFile?.length) {
      data.ExternalFile = externalFile;
    }

    saveAccountInfo(
      {
        ...data,
        Id: accountFormInputs?.Id ? accountFormInputs.Id : 0,
        AnswerScriptDetailType:
          data.AnswerScriptDetailType === 0 && !!data.AnswerScriptDetails
            ? 1
            : data.AnswerScriptDetailType,
      },
      {
        onSuccess: (mutationResult: TResult, data: any) => {
          if (!data.Id && mutationResult.Data.Id) {
            reset(); // clear the form
            setExternalFile('');
            history.replace(
              `/home/accounts/edit/${mutationResult.Data.Id}/accountInfo`,
              { ID: mutationResult.Data.Id },
            );
          } else if (data?.Id && mutationResult.Data.Id) {
            showSuccessToast({
              message: 'Your changes were successfully saved.',
              autoClose: 3000,
            });
            setExternalFile('');
            if (infoMessage === true)
              confirmAlert({
                message:
                  'Please be aware that updates to procedure links will happen in the background and may take up to five minutes and all procedures for this account will be re-published through this process.',
                buttons: [
                  {
                    label: 'Ok',
                    onClick: () => {
                      history.push(
                        `/home/accounts/edit/${mutationResult.Data.Id}/accountInfo`,
                        { ID: mutationResult.Data.Id },
                      );
                    },
                    className: 'pr-btn-primary',
                  },
                ],
              });
            setTimeout(() => {
              setDirty(false);
            }, 500);
          } else if (mutationResult.Errors[0].Message) {
            showErrorToast({
              message: mutationResult.Errors[0].Message,
            });
          } else {
            showErrorToast({
              message:
                'An error occurred trying to save your changes, please try again.',
            });
          }
        },
        onError: (e: ErrorModal) => {
          showErrorToast({
            message: e.Errors[0].Message,
          });
        },
      },
    );
  };

  const handleSave = async (data: AccountFormInputs) => {
    let infoMessage = false;
    if (accountInfo === undefined) infoMessage = false;
    else if (
      data.CID !== accountInfo?.accountFormInputs.CID ||
      data.SID !== accountInfo?.accountFormInputs.SID ||
      data.PID !== accountInfo?.accountFormInputs.PID
    ) {
      infoMessage = true;
    }
    if (
      data?.AnswerScriptDetailType === 2 &&
      data?.AnswerScriptDetails === ''
    ) {
      showErrorToast({
        message: 'Please select a procedure in answer script details.',
      });
    } else if (
      data?.AnswerScriptDetailType === 3 &&
      (data?.AnswerScriptDetails === '' ||
        data?.AnswerScriptDetails === undefined)
    ) {
      showErrorToast({
        message: 'Please select an external document in answer script details.',
      });
    } else {
      if (infoMessage === true) {
        confirmAlert({
          message:
            "You are about to change an account's CID, SID, or PID. Are you sure you want to proceed?",
          buttons: [
            {
              label: 'Yes',
              onClick: () => {
                saveFormApi(data, infoMessage);
              },
              className: 'pr-btn-primary',
            },
            {
              label: 'No',
              onClick: () => {
                //
              },
            },
          ],
        });
      } else {
        await saveFormApi(data, infoMessage);
      }
    }
  };

  const onSubmit = (data: AccountFormInputs) => {
    const checkifRMexists = relationshipManagers?.find(
      (manager: any) => manager.id === data.RelationshipManagerId,
    );
    const checkIfTimeZoneIdExists = timezones?.find(
      (timezone: any) => timezone.id === data.TimeZoneId,
    );
    const checkIfStateProvinceIdExists = stateProvinces?.find(
      (stateProvince: any) => stateProvince.id === data.StateProvinceId,
    );
    if (checkifRMexists === undefined) {
      data.RelationshipManagerId = null;
    }
    if (checkIfTimeZoneIdExists === undefined) {
      data.TimeZoneId = null;
    }
    if (checkIfStateProvinceIdExists === undefined) {
      data.StateProvinceId = null;
    }
    const deadLine = AutoPurgeDeadlineInHours && +AutoPurgeDeadlineInHours;
    const initialDeadLine =
      accountInfoReturn && accountInfoReturn.Data.AutoPurgeDeadlineInHours;
    if (isAlertLimitEnabled) {
      // remove the key without TypeScript complaining by asserting to any
      delete (data as any).PersonAlertLimiter;
      data.AccountCategoryLimitingValues =
        data.AccountCategoryLimitingValues?.map(x => ({
          ...x,
          Id: String(x.Id) === '' ? 0 : Number(x.Id),
        }));
    }
    data.ReportingDisplayName = data.ReportingDisplayName?.trim();
    data.Name = data.Name?.trim();

    if (isAutoPurgeEnabled && initialDeadLine !== deadLine && deadLine !== 72) {
      confirmAlert({
        message:
          'Warning! Changing Auto-Purge settings will affect what calls get DELETED or RETAINED. Are you sure that you wish to change these settings?',
        buttons: [
          {
            label: 'Yes',
            onClick: () => {
              setDirty(false);
              handleSave(data);
            },
            className: 'pr-btn-primary',
          },
          {
            label: 'No',
            onClick: () => {
              //
            },
          },
        ],
      });
    } else {
      setDirty(false);
      handleSave(data);
    }
  };

  function toggleFileImage() {
    setShowFileImage(showFileImage => !showFileImage);
  }

  usePrompt(
    isPageDirty,
    'Are you sure you want to leave? Changes that you made may not be saved.',
  );

  const colorScheme = useApplicationThemeStore(
    React.useCallback((state: any) => state.colourScheme, []),
  );

  // Custom duplicate check helper
  const isDuplicate = (name: string, index: number) => {
    const trimmed = name.trim().toLowerCase();
    return limiterOptions?.some(
      (item, i) => i !== index && item.Name.trim().toLowerCase() === trimmed,
    );
  };
  // Function to handle adding a new limiting value field
  const onAddNewField = async (index: number) => {
    if (index === 0) {
      const lastInput = containerRef.current?.querySelector(
        `input[name="AccountCategoryLimitingValues.${0}.Name"]`,
      ) as HTMLInputElement | null;

      if (lastInput) {
        lastInput.dispatchEvent(new Event('blur', { bubbles: true }));
      }
    }

    // If valid, then append the new field
    append({ Id: 0, Name: '' });
    await trigger('AccountCategoryLimitingValues');

    // Focus on the new input after it renders
    setTimeout(() => {
      const input = document.querySelector(
        `input[name="AccountCategoryLimitingValues.${fields.length}.Name"]`,
      ) as HTMLInputElement | null;
      input?.focus();
    }, 0);
  };

  // Check if any required errors exist in the limiting values
  const hasRequiredError = useMemo(() => {
    return fields.some((_, index) => {
      const errorType =
        errors.AccountCategoryLimitingValues?.[index]?.Name?.type;
      return (
        errorType === 'required' ||
        errorType === 'duplicate' ||
        errorType === 'requiredTrimmed'
      );
    });
  }, [errors.AccountCategoryLimitingValues, fields]);

  return (
    <>
      <Flex
        as="form"
        flexDirection="column"
        height="100%"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Box
          style={{
            flex: '1 1 auto',
            flexGrow: 1,
            overflow: 'auto',
            height: '1px',
          }}
        >
          <Card p={4}>
            <Stack direction="y" gap={4}>
              <Grid minColWidth="350px" gap={4}>
                <FormInput
                  feedback={errors.VDN?.message}
                  validation={errors.VDN ? 'error' : undefined}
                  register={register}
                  type="text"
                  label="Start Code"
                  name="VDN"
                  length={4}
                  maxLength={4}
                  validate={{
                    validate: (value: any) => {
                      if (value.indexOf(' ') >= 0)
                        return 'Spaces are not allowed.';
                    },
                  }}
                  required={isLiveEnabled ? true : false}
                />
                <FormInput
                  feedback={errors.CID?.message}
                  validation={errors.CID ? 'error' : undefined}
                  register={register}
                  label="CID"
                  type="text"
                  required
                  maxLength={4}
                  validate={{
                    validate: (value: any) => {
                      if (value.indexOf(' ') >= 0)
                        return 'Spaces are not allowed.';
                    },
                  }}
                />
                <FormInput
                  feedback={errors.SID?.message}
                  validation={errors.SID ? 'error' : undefined}
                  register={register}
                  label="SID"
                  type="text"
                  required
                  maxLength={4}
                  validate={{
                    validate: (value: any) => {
                      if (value.indexOf(' ') >= 0)
                        return 'Spaces are not allowed.';
                    },
                  }}
                />
                <FormInput
                  feedback={errors.PID?.message}
                  validation={errors.PID ? 'error' : undefined}
                  register={register}
                  label="PID"
                  type="text"
                  required
                  maxLength={4}
                  validate={{
                    validate: (value: any) => {
                      if (value.indexOf(' ') >= 0)
                        return 'Spaces are not allowed.';
                    },
                  }}
                />
                <Controller
                  render={(props: {
                    onChange: (value: number) => void;
                    value: number;
                  }) => {
                    return (
                      <FormSelect
                        value={props.value}
                        label="Relationship Manager"
                        options={relationshipManagers}
                        onChange={(e: SyntheticEvent<HTMLSelectElement>) => {
                          props.onChange(parseInt(e.currentTarget.value, 10));
                        }}
                      />
                    );
                  }}
                  defaultValue={''}
                  name="RelationshipManagerId"
                  control={control}
                />

                <Controller
                  render={(props: {
                    onChange: (value: number) => void;
                    value: number;
                  }) => {
                    return (
                      <FormSelect
                        value={props.value}
                        feedback={errors.StatusId?.message}
                        validation={
                          errors.StatusId?.message ? 'error' : undefined
                        }
                        label="Status"
                        options={accountStatuses}
                        onChange={(e: SyntheticEvent<HTMLSelectElement>) => {
                          props.onChange(parseInt(e.currentTarget.value, 10));
                        }}
                        required
                      />
                    );
                  }}
                  rules={{
                    required: 'The Status field is required.',
                    validate: {
                      invalid: (value: any) => {
                        if (Number.isNaN(value))
                          return 'The Status field is required.';
                      },
                    },
                  }}
                  defaultValue={''}
                  name="StatusId"
                  control={control}
                />

                <Controller
                  render={(props: {
                    onChange: (value: number) => void;
                    value: number;
                  }) => {
                    return (
                      <FormSelect
                        value={props.value}
                        feedback={errors.AccountServiceId?.message}
                        validation={
                          errors.AccountServiceId?.message ? 'error' : undefined
                        }
                        label="Service Type"
                        options={accountServices}
                        onChange={(e: SyntheticEvent<HTMLSelectElement>) => {
                          const value = parseInt(e.currentTarget.value, 10);
                          props.onChange(value);
                        }}
                        required
                      />
                    );
                  }}
                  rules={{
                    required: 'The Service Type field is required.',
                    validate: {
                      invalid: (value: any) => {
                        if (Number.isNaN(value))
                          return 'The Service Type field is required.';
                      },
                    },
                  }}
                  defaultValue={''}
                  name="AccountServiceId"
                  control={control}
                />

                <Controller
                  render={(props: {
                    onChange: (value: number) => void;
                    value: number;
                  }) => (
                    <FormSelect
                      value={props.value}
                      label="State/Province"
                      options={stateProvinces}
                      onChange={(e: SyntheticEvent<HTMLSelectElement>) => {
                        props.onChange(parseInt(e.currentTarget.value, 10));
                      }}
                    />
                  )}
                  defaultValue={''}
                  name="StateProvinceId"
                  control={control}
                />
                <FormInput register={register} label="City" type="text" />
                <Controller
                  render={(props: {
                    onChange: (value: number) => void;
                    value: number;
                  }) => {
                    return (
                      <FormSelect
                        value={props.value}
                        label="Time Zone"
                        options={timezones}
                        onChange={(e: SyntheticEvent<HTMLSelectElement>) => {
                          props.onChange(parseInt(e.currentTarget.value, 10));
                        }}
                      />
                    );
                  }}
                  defaultValue={''}
                  name="TimeZoneId"
                  control={control}
                />

                <FormInput
                  name="Name"
                  register={register}
                  label="Account Name"
                  type="text"
                  maxLength={500}
                />
                <FormInput
                  name="AnswerScript"
                  register={register}
                  label="Answer Script"
                  type="text"
                />
                <FormInput
                  name="ReportingDisplayName"
                  register={register}
                  label="Reporting Display Name"
                  type="text"
                  maxLength={500}
                  disabled={
                    !(
                      permission.isRelationshipManager ||
                      permission.isSuperAdmin
                    )
                  }
                />
              </Grid>
              <CheckboxGrid>
                <FormGroup style={{ gridArea: '1 / 1 / 2 / 2' }}>
                  <Flex height="100%" pt={7} alignItems="flex-start">
                    <Checkbox
                      name="IsPriorityFinalization"
                      register={register}
                      label="Priority Finalization"
                    />
                  </Flex>
                </FormGroup>
                <FormGroup style={{ gridArea: '1 / 2 / 2 / 3' }}>
                  <Flex height="100%" pt={7} alignItems="flex-start">
                    <Checkbox
                      name="IsLive"
                      register={register}
                      label="Is Live?"
                    />
                  </Flex>
                </FormGroup>
                <FormGroup style={{ gridArea: '2 / 1 / 3 / 2' }}>
                  <Flex height="100%" pt={7} flexDirection="column">
                    <Controller
                      render={(props: {
                        onChange: (value: boolean) => void;
                        value: boolean;
                      }) => {
                        return (
                          <Checkbox
                            name="IsAutoPurgeEnabled"
                            disabled={!purgePermission}
                            label="Auto-Purge Enabled?"
                            checked={isAutoPurgeEnabled}
                            handleChange={() => {
                              confirmAlert({
                                message:
                                  'Warning! Changing Auto-Purge settings will affect what calls get DELETED or RETAINED. Are you sure that you wish to change these settings?',
                                buttons: [
                                  {
                                    label: 'Yes',
                                    onClick: () => {
                                      props.onChange(!isAutoPurgeEnabled);
                                    },
                                    className: 'pr-btn-primary',
                                  },
                                  {
                                    label: 'No',
                                    onClick: () => {
                                      //
                                    },
                                  },
                                ],
                              });
                            }}
                          />
                        );
                      }}
                      defaultValue={false}
                      name="IsAutoPurgeEnabled"
                      control={control}
                    />

                    {isAutoPurgeEnabled === true && (
                      <FormInput
                        name="AutoPurgeDeadlineInHours"
                        register={register}
                        label="Auto-Purge Deadline in Hours"
                        type="text"
                        feedback={errors.AutoPurgeDeadlineInHours?.message}
                        validation={
                          errors.AutoPurgeDeadlineInHours?.message
                            ? 'error'
                            : undefined
                        }
                        validate={{
                          validate: (value: any) => {
                            if (parseInt(value) === 0)
                              return 'Auto-Purge Deadline cannot be zero.';
                            if (Number.isNaN(parseInt(value)))
                              return 'Auto-Purge Deadline cannot be empty.';
                          },
                        }}
                      />
                    )}
                  </Flex>
                </FormGroup>
                <>
                  <FormGroup
                    style={{
                      gridArea: '2 / 2 / 3 / 3',
                      paddingBottom: '32px',
                    }}
                  >
                    <Flex height="100%" pt={7} alignItems="flex-start">
                      <Checkbox
                        name="PersonAlertLimiter"
                        register={register}
                        label="Person Alert Limiter"
                        disabled={
                          !(permission.isSuperAdmin || permission.isAdmin)
                        }
                        title={
                          !(permission.isSuperAdmin || permission.isAdmin)
                            ? 'You don’t have permission to set the Personal Alert Limiter.'
                            : ''
                        }
                        handleChange={(e) => {
                          if (!e.currentTarget.checked) {
                            const values =
                              accountFormInputs?.AccountCategoryLimitingValues?.length
                                ? accountFormInputs.AccountCategoryLimitingValues
                                : [{ Id: 0, Name: '', AccountId: null }];
                            reset({
                              ...getValues(),
                              LimitCategory: accountFormInputs?.AccountCategoryLimitingValues?.length ? accountFormInputs.LimitCategory : '',
                              AccountCategoryLimitingValues: values,
                            });
                          }
                        }
                        }
                      />
                      <Icon.Info
                        className="info-icon"
                        title="This feature allows you to limit the view/setup of personal alerts based on the criteria selected inside the Limiting Factor options"
                      />
                    </Flex>
                  </FormGroup>
                  {isAlertLimitEnabled && (
                    <>
                      <FormInput
                        name="LimitCategory"
                        register={register}
                        label="Limiting Factor"
                        type="text"
                        style={{
                          gridArea: '2 / 3 / 3 / 4',
                          width: '86%',
                          visibility: 'visible',
                          opacity: 1,
                          transition: 'opacity 0.25s ease',
                          pointerEvents: 'auto',
                        }}
                        disabled={
                          permission.isSuperAdmin ||
                          permission.isAdmin ||
                          permission.isRelationshipManager
                            ? false
                            : true
                        }
                        maxLength={250}
                        validation={errors.LimitCategory ? 'error' : undefined}
                        feedback={errors.LimitCategory?.message}
                        required={isAlertLimitEnabled}
                        title={
                          !(
                            permission.isSuperAdmin ||
                            permission.isAdmin ||
                            permission.isRelationshipManager
                          )
                            ? 'You don’t have permission to set the Personal Alert Limiter.'
                            : ''
                        }
                        validate={{
                          validate: {
                            noLeadingSpace: (value: string) =>
                              value.trimStart() === value ||
                              'Leading spaces are not allowed.',

                            noOnlySpaces: (value: string) =>
                              value.trim() !== '' ||
                              'Limiting Factor cannot be only spaces.',
                          },
                        }}
                      />

                      <div
                        style={{
                          transition: 'opacity 0.25s ease',
                          pointerEvents: 'auto',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '10px',
                          width: '100%',
                          gridArea: '2 / 4 / 3 / 5',
                        }}
                      >
                        {fields.map((field, index) => (
                          <div key={field.id} ref={containerRef}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                width: '100%',
                              }}
                            >
                              <FormInput
                                display="none"
                                type="number"
                                register={register}
                                name={`AccountCategoryLimitingValues.${index}.Id`}
                                key={`${field.id}${index}${field?.Name}`}
                                disabled={
                                  permission.isSuperAdmin ||
                                  permission.isAdmin ||
                                  permission.isRelationshipManager
                                    ? false
                                    : true
                                }
                              />
                              <FormInput
                                label={
                                  index === 0 ? 'Limiting Factor Options' : ''
                                }
                                disabled={
                                  permission.isSuperAdmin ||
                                  permission.isAdmin ||
                                  permission.isRelationshipManager
                                    ? false
                                    : true
                                }
                                title={
                                  !(
                                    permission.isSuperAdmin ||
                                    permission.isAdmin ||
                                    permission.isRelationshipManager
                                  )
                                    ? 'You don’t have permission to set the Personal Alert Limiter.'
                                    : ''
                                }
                                maxLength={250}
                                register={register}
                                type="text"
                                name={`AccountCategoryLimitingValues.${index}.Name`}
                                required={isAlertLimitEnabled}
                                validation={
                                  errors.AccountCategoryLimitingValues?.[index]
                                    ? 'error'
                                    : undefined
                                }
                                feedback={
                                  errors.AccountCategoryLimitingValues?.[index]
                                    ?.Name?.message
                                }
                                style={{ width: '86%', marginBottom: '5px' }}
                                validate={{
                                  required: {
                                    value: isAlertLimitEnabled,
                                    message:
                                      'Limiting Factor Options is required',
                                  },
                                  validate: {
                                    requiredTrimmed: (value: string) =>
                                      value.trim().length > 0 ||
                                      'Limiting Factor Options is required',

                                    duplicate: (value: string) =>
                                      isDuplicate(value, index)
                                        ? 'Duplicate names are not allowed'
                                        : true,
                                  },
                                }}
                                onTextChange={(value: any) => {
                                  setValue(
                                    `AccountCategoryLimitingValues.${index}.Name`,
                                    value,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                  // trigger all other fields to revalidate duplicates
                                  limiterOptions?.forEach((_, i) => {
                                    trigger(
                                      `AccountCategoryLimitingValues.${i}.Name`,
                                    );
                                  });
                                }}
                              />
                              {fields.length > 1 && (
                                <Button
                                  variant="ghostDanger"
                                  iconBefore={
                                    <Icon.X
                                      title={
                                        !(
                                          permission.isSuperAdmin ||
                                          permission.isAdmin ||
                                          permission.isRelationshipManager
                                        )
                                          ? 'You don’t have permission to remove the Personal Alert Limiter.'
                                          : 'Remove'
                                      }
                                    />
                                  }
                                  title="Remove"
                                  style={{
                                    marginTop: index === 0 ? '21px' : '0px',
                                  }}
                                  onClick={() => {
                                    const currentValues = getValues();
                                    const updatedOptions =
                                      currentValues?.AccountCategoryLimitingValues?.filter(
                                        (_: any, i: number) => i !== index,
                                      );

                                    reset({
                                      ...currentValues,
                                      AccountCategoryLimitingValues:
                                        updatedOptions,
                                    });
                                    trigger('AccountCategoryLimitingValues');
                                    remove(index);
                                    setTimeout(() => {
                                      const next = document.querySelector(
                                        `input[name="AccountCategoryLimitingValues.${index}.Name"]`,
                                      ) as HTMLInputElement;
                                      next?.focus();
                                    }, 0);
                                  }}
                                  disabled={
                                    fields.length <= 1 ||
                                    !(
                                      permission.isSuperAdmin ||
                                      permission.isAdmin ||
                                      permission.isRelationshipManager
                                    )
                                  }
                                />
                              )}
                            </div>
                            {index === fields.length - 1 && (
                              <Button
                                variant="link"
                                iconBefore={<Icon.Add />}
                                title={
                                  !(
                                    permission.isSuperAdmin ||
                                    permission.isAdmin ||
                                    permission.isRelationshipManager
                                  )
                                    ? 'You don’t have permission to remove the Personal Alert Limiter.'
                                    : fields.length === 20
                                    ? 'Limit exceeded. You can add up to 20 options.'
                                    : hasRequiredError ||
                                      (index === 0 &&
                                        limiterOptions?.[0]?.Name.trim() === '')
                                    ? 'Please complete the required field before adding a new field.'
                                    : 'Add new field '
                                }
                                size="sm"
                                style={{ marginTop: '10px' }}
                                onClick={() => onAddNewField(index)}
                                disabled={
                                  !(
                                    permission.isSuperAdmin ||
                                    permission.isAdmin ||
                                    permission.isRelationshipManager
                                  ) ||
                                  fields.length >= 20 ||
                                  hasRequiredError ||
                                  limiterOptions?.[index]?.Name.trim() === ''
                                }
                              >
                                Add new field
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              </CheckboxGrid>
              <Box>
                <FormLabel htmlFor="scriptDetails">
                  Answer Script Details
                </FormLabel>
                <Box my={2}>
                  <Stack direction="x" gap={3}>
                    <Controller
                      render={(props: {
                        onChange: (value: number) => void;
                        value: number;
                      }) => {
                        return (
                          <RadioButton
                            onChange={() => {
                              props.onChange(1);
                            }}
                            inline
                            label="Text"
                            name="AnswerScriptDetailType"
                            checked={
                              answerScriptDetailType === 1 ||
                              answerScriptDetailType === 0
                            }
                          />
                        );
                      }}
                      defaultValue={1}
                      name="AnswerScriptDetailType"
                      control={control}
                    />

                    {accountId && (
                      <Controller
                        render={(props: {
                          onChange: (value: number) => void;
                          value: number;
                        }) => {
                          return (
                            <RadioButton
                              onChange={() => {
                                props.onChange(2);
                              }}
                              inline
                              label="Procedure"
                              name="AnswerScriptDetailType"
                              checked={answerScriptDetailType === 2}
                            />
                          );
                        }}
                        defaultValue={1}
                        name="AnswerScriptDetailType"
                        control={control}
                      />
                    )}

                    <Controller
                      render={(props: {
                        onChange: (value: number) => void;
                        value: number;
                      }) => {
                        return (
                          <RadioButton
                            onChange={() => {
                              props.onChange(3);
                            }}
                            inline
                            label="External Document"
                            name="AnswerScriptDetailType"
                            checked={answerScriptDetailType === 3}
                          />
                        );
                      }}
                      defaultValue={1}
                      name="AnswerScriptDetailType"
                      control={control}
                    />
                  </Stack>
                </Box>
                {(answerScriptDetailType === 1 ||
                  answerScriptDetailType === 0) && (
                  <Controller
                    render={(props: {
                      onChange: (value: any) => void;
                      value: any;
                    }) => {
                      return (
                        <TinyMCE
                          colorScheme={colorScheme}
                          toggleFileImage={toggleFileImage}
                          setLoading={setTinyMCELoading}
                          height={240}
                          value={props.value}
                          name="AnswerScriptDetails"
                          onChange={(content: string) => {
                            props.onChange(content);
                          }}
                        />
                      );
                    }}
                    defaultValue={''}
                    name="AnswerScriptDetails"
                    control={control}
                  />
                )}
                {answerScriptDetailType === 2 && (
                  <Controller
                    render={(props: {
                      onChange: (value: string) => void;
                      value: any;
                    }) => {
                      return (
                        <StyledSelect
                          onChange={(e: SyntheticEvent<HTMLSelectElement>) => {
                            props.onChange(e.currentTarget.value);
                          }}
                          value={props.value}
                          style={{ maxWidth: '258px' }}
                        >
                          <option value="" selected>
                            Select one
                          </option>
                          {procedures &&
                            procedures.Data.map((item: any) => (
                              <option key={item} value={item}>
                                {item}
                              </option>
                            ))}
                        </StyledSelect>
                      );
                    }}
                    defaultValue={''}
                    name="AnswerScriptDetails"
                    control={control}
                  />
                )}
                {answerScriptDetailType === 3 && (
                  <Controller
                    render={(props: {
                      onChange: (value?: string) => void;
                      value: string;
                    }) => {
                      return (
                        <Dropzone
                          info="Please ensure this file type is associated with a program in the Citrix environment"
                          value={props.value}
                          accountId={
                            accountInfoReturn && accountInfoReturn.Data.Id
                          }
                          onChange={(file?: any) => {
                            if (!file) props.onChange();
                            if (file) {
                              const fileExtension = file?.name.split('.');
                              const validExtensions = [
                                'jpg',
                                'jpeg',
                                'png',
                                'svg',
                                'txt',
                                'docx',
                                'doc',
                                'pdf',
                                'html',
                                'htm',
                              ];
                              const checkCondtion = validExtensions.includes(
                                fileExtension[fileExtension.length - 1],
                              );
                              if (!checkCondtion) {
                                showErrorToast({
                                  message:
                                    'Files of this extension cannot be uploaded',
                                });
                              } else {
                                const reader = new FileReader();
                                reader.onabort = () =>
                                  console.log('file reading was aborted');
                                reader.onerror = () =>
                                  console.log('file reading has failed');
                                reader.onload = () => {
                                  const base64String = reader.result;
                                  if (typeof base64String === 'string') {
                                    props.onChange(file.name);
                                    setExternalFile(base64String);
                                  }
                                };
                                reader.readAsDataURL(file);
                              }
                            }
                          }}
                        />
                      );
                    }}
                    defaultValue={''}
                    name="AnswerScriptDetails"
                    control={control}
                  />
                )}
              </Box>
            </Stack>
          </Card>
        </Box>
        <Flex justifyContent="flex-end" mt={3}>
          <Stack direction="x" gap={2}>
            <Button
              variant="secondary"
              onClick={() => {
                reset();
                history.push('/home/accounts');
              }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              Save
            </Button>
          </Stack>
        </Flex>
      </Flex>

      <FileImage
        accountId={parseInt(accountId)}
        filesOnly
        buttonText={'Add to answer script details'}
        account={`${accountInfoReturn?.Data.CID}${'-'}${
          accountInfoReturn?.Data.SID
        }${'-'}${accountInfoReturn?.Data.PID}`}
        showPanel={showFileImage}
        togglePanel={toggleFileImage}
        createFolder={createBlobDirectory}
        uploadFile={uploadFile}
        deleteFile={deleteFile}
        addFileToEditor={(file: BlobType) => {
          if (file.Type !== 'image') {
            const hrefTag =
              "<a href='" +
              `${apiBaseUrl}/procedures/root/${file.Path}` +
              "/'>" +
              file.Name +
              '</a>';
            (window as any)?.tinymce?.activeEditor?.insertContent(hrefTag);
          } else {
            const hrefTag = `<img src="${file.Uri}" />`;
            (window as any)?.tinymce?.activeEditor?.insertContent(hrefTag);
          }
        }}
      />
      {isLoadingInfo || isLoadingFile ? <Loader /> : null}
    </>
  );
}
